import { useState } from "react";
import { useLanguage } from "@/lib/language-context";
import { useApp } from "@/lib/app-context";
import { visaService, type ApplicationStatus } from "@/lib/visa-service";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Clock, CheckCircle, Headphones, FileSearch, MessageCircle, RotateCcw, Loader2 } from "lucide-react";

const RESUMABLE_STATUSES: ApplicationStatus[] = [
  "draft",
  "step2_completed",
  "step3_completed",
  "step4_completed",
  "step5_completed",
  "step6_completed",
  "step7_completed",
];

export function HomeScreen() {
  const { t } = useLanguage();
  const { setCurrentScreen, setSelectedCountry, setApplicationId, setVisaType, setResumeApplicationData } = useApp();
  const [showContinueForm, setShowContinueForm] = useState(false);
  const [continueAppNumber, setContinueAppNumber] = useState("");
  const [continueError, setContinueError] = useState<string | null>(null);
  const [isContinueLoading, setIsContinueLoading] = useState(false);

  const handleStartApplication = () => {
    setApplicationId(null);
    setVisaType(null);
    setResumeApplicationData(null);
    setSelectedCountry("india");
    setCurrentScreen("apply-passport");
  };

  const handleContinueApplication = async () => {
    const num = continueAppNumber.trim().toUpperCase();
    if (!num) {
      setContinueError((t as Record<string, string>).continueApplicationEnterNumber ?? "Enter your application number");
      return;
    }
    setContinueError(null);
    setIsContinueLoading(true);
    try {
      const app = await visaService.getApplication(num);
      if (!RESUMABLE_STATUSES.includes(app.status as ApplicationStatus)) {
        setContinueError(
          (t as Record<string, string>).continueApplicationAlreadySubmitted ??
            "This application is already submitted. Use Check Status to view it."
        );
        setIsContinueLoading(false);
        return;
      }
      setApplicationId(app.application_number);
      if (app.visa_type) setVisaType(app.visa_type);
      // Store the fetched application data to avoid re-fetching in PassportUpload
      setResumeApplicationData(app);
      setSelectedCountry("india");
      setCurrentScreen("apply-passport");
    } catch {
      setContinueError(
        (t as Record<string, string>).continueApplicationNotFound ?? "Application not found. Check the number and try again."
      );
    } finally {
      setIsContinueLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-80px)]">
      {/* Hero Section */}
      <section className="relative px-4 py-6 bg-background">
        <div className="max-w-lg mx-auto text-center">
          {/* 72 Hours Badge - Key selling point */}
          {/* <div className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full mb-4 shadow-md">
            <Clock className="h-5 w-5" />
            <span className="font-bold text-lg">{t.processingTime}</span>
          </div> */}

          {/* Main Heading */}
          <h1 className="text-3xl sm:text-4xl font-bold text-primary mb-2 leading-tight text-balance">
            {t.welcomeTitle}
          </h1>
          
          {/* Visa Type */}
          {/* <p className="text-lg text-foreground mb-1">{t.heroDescription}</p> */}
          <p className="text-base text-muted-foreground mb-4">{t.welcomeSubtitle}</p>

          {/* Price - Very Prominent */}
          {/* <div className="mb-6">
            <span className="text-4xl sm:text-5xl font-bold text-primary">{t.price}</span>
          </div> */}

          {/* Primary CTA - Extra Large for elderly users */}
          <Button
            onClick={handleStartApplication}
            size="lg"
            className="w-full min-h-16 py-4 text-xl font-bold rounded-2xl shadow-lg hover:shadow-xl transition-shadow mb-3 whitespace-normal"
          >
            {t.getVisaNow || "Get Your Visa Now"}
          </Button>

          {/* WhatsApp Help - Prominent for elderly users who get stuck */}
          <a
            href={`https://wa.me/${t.phoneNumberRaw}`}
            className="flex items-center justify-center gap-2 w-full min-h-12 py-3 bg-[#25D366] text-white font-semibold rounded-xl mb-4 hover:opacity-90 transition-opacity whitespace-normal text-center"
          >
            <MessageCircle className="h-5 w-5 flex-shrink-0" />
            <span>{t.needHelpWhatsApp || "Need help? WhatsApp us"}</span>
          </a>

          {/* Continue Application */}
          {!showContinueForm ? (
            <Button
              onClick={() => setShowContinueForm(true)}
              variant="outline"
              size="lg"
              className="w-full min-h-12 py-3 text-base font-medium rounded-xl border-2 bg-transparent whitespace-normal mb-3"
            >
              <RotateCcw className="h-5 w-5 mr-2 flex-shrink-0" />
              {(t as Record<string, string>).continueApplication ?? "Continue Application"}
            </Button>
          ) : (
            <div className="mb-3 p-4 rounded-xl border-2 border-border bg-muted/50 space-y-3">
              <p className="text-sm text-muted-foreground">
                {(t as Record<string, string>).continueApplicationDesc ?? "Enter your application number to continue where you left off."}
              </p>
              <Input
                placeholder={(t as Record<string, string>).continueApplicationPlaceholder ?? "e.g. VIM-2026-171522"}
                value={continueAppNumber}
                onChange={(e) => setContinueAppNumber(e.target.value)}
                className="font-mono uppercase"
                disabled={isContinueLoading}
              />
              {continueError && (
                <p className="text-sm text-destructive">{continueError}</p>
              )}
              <div className="flex gap-2">
                <Button
                  onClick={handleContinueApplication}
                  disabled={isContinueLoading}
                  className="flex-1"
                >
                  {isContinueLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  {(t as Record<string, string>).continueApplicationSubmit ?? "Continue"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowContinueForm(false);
                    setContinueAppNumber("");
                    setContinueError(null);
                  }}
                  disabled={isContinueLoading}
                >
                  {(t as Record<string, string>).cancel ?? "Cancel"}
                </Button>
              </div>
            </div>
          )}

          {/* Secondary CTA */}
          <Button
            onClick={() => setCurrentScreen("status")}
            variant="outline"
            size="lg"
            className="w-full min-h-12 py-3 text-base font-medium rounded-xl border-2 bg-transparent whitespace-normal"
          >
            <FileSearch className="h-5 w-5 mr-2 flex-shrink-0" />
            {t.checkExistingStatus}
          </Button>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 py-8 bg-muted">
        <div className="max-w-lg mx-auto">
          <div className="grid gap-4">
            {/* Feature 1 */}
            <Card className="p-5 flex items-start gap-4 border-0 shadow-sm">
              <div className="flex-shrink-0 w-14 h-14 rounded-full bg-secondary flex items-center justify-center">
                <CheckCircle className="h-7 w-7 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground mb-1">{t.feature1Title}</h3>
                <p className="text-base text-muted-foreground">{t.feature1Desc}</p>
              </div>
            </Card>

            {/* Feature 2 */}
            <Card className="p-5 flex items-start gap-4 border-0 shadow-sm">
              <div className="flex-shrink-0 w-14 h-14 rounded-full bg-secondary flex items-center justify-center">
                <Clock className="h-7 w-7 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground mb-1">{t.feature2Title}</h3>
                <p className="text-base text-muted-foreground">{t.feature2Desc}</p>
              </div>
            </Card>

            {/* Feature 3 */}
            <Card className="p-5 flex items-start gap-4 border-0 shadow-sm">
              <div className="flex-shrink-0 w-14 h-14 rounded-full bg-secondary flex items-center justify-center">
                <Headphones className="h-7 w-7 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground mb-1">{t.feature3Title}</h3>
                <p className="text-base text-muted-foreground">{t.feature3Desc}</p>
              </div>
            </Card>
          </div>
        </div>
      </section>

</div>
  );
}
